// Transporte a la Zebra GT800 por USB usando el agente Zebra Browser Print.
// NO requiere bajar ni instalar el SDK .js: se le habla directo al servicio local
// de Browser Print (que el operador ya instaló en la PC de la impresora).
//
// - En https (sitio en la nube): usa el canal seguro del agente (https://127.0.0.1:9101).
// - En http (localhost / dev): usa http://127.0.0.1:9100.
// - Si además está cargado el SDK oficial (window.BrowserPrint), se usa primero.
// - Si nada responde, el llamador ofrece descargar el .zpl (fallback).

// Vía opcional: SDK oficial, solo si alguien lo cargó en la página.
function imprimirConSDK(zpl) {
  return new Promise((resolve, reject) => {
    const BP = typeof window !== "undefined" ? window.BrowserPrint : null;
    if (!BP || !BP.getDefaultDevice) return reject(new Error("SDK no disponible"));
    BP.getDefaultDevice(
      "printer",
      (device) => {
        if (!device) return reject(new Error("Sin impresora por defecto"));
        device.send(zpl, () => resolve("sdk"), (e) => reject(new Error(e || "Error al enviar")));
      },
      (e) => reject(new Error(e || "No se pudo obtener la impresora"))
    );
  });
}

// Endpoints del agente Browser Print. Se prueban todos: el http (9100) es el que
// suele estar activo; para usarlo desde la nube (https) hay que permitir
// "contenido no seguro" en Chrome para el sitio (mixed content).
const BASES_AGENTE = [
  "http://localhost:9100",
  "http://127.0.0.1:9100",
  "https://localhost:9101",
  "https://127.0.0.1:9101",
];

// ¿El nombre/uid parece una impresora Zebra (que entiende ZPL)?
function esZebra(d) {
  const txt = `${(d && d.name) || ""} ${(d && d.uid) || ""} ${
    (d && d.deviceType) || ""
  }`.toLowerCase();
  return /zebra|zdesigner|gt800|zpl|gk420|gx420|zd\d/.test(txt);
}

// Obtiene la impresora a usar del agente. Manda SIEMPRE a la impresora que el
// cliente eligió como "Default Device" en Browser Print (botón "Cambiar").
//   1) Usa la default del agente (lo que el cliente configuró).
//   2) Si no hay default, prefiere una Zebra de la lista de disponibles.
//   3) Si no, usa la primera impresora disponible.
async function obtenerDispositivo(base) {
  let porDefecto = null;
  try {
    const r = await fetch(`${base}/default?type=printer`);
    if (r.ok) {
      const d = await r.json();
      if (d && d.uid) porDefecto = d;
    }
  } catch (_) {
    /* intenta /available abajo */
  }
  // Respeta lo que el cliente puso como predeterminada en Browser Print.
  if (porDefecto) return porDefecto;

  let lista = [];
  try {
    const r2 = await fetch(`${base}/available`);
    if (r2.ok) {
      const j = await r2.json();
      lista = (j && j.printer) || [];
    }
  } catch (_) {
    /* usa lo que haya */
  }
  // Sin default marcada: prefiere una Zebra; si no, la primera disponible.
  const zebra = lista.find(esZebra);
  if (zebra) return zebra;
  if (lista.length) return lista[0];
  throw new Error("Sin impresora en el agente");
}

// ===== Envío por LOTES =====
// Un trabajo de 125 etiquetas son ~130 KB de ZPL. Mandado en un solo POST, el buffer
// del agente/impresora se satura y la impresión sale incompleta. Por eso el ZPL se
// parte en bloques ^XA..^XZ y se manda en tandas chicas, esperando cada una.
const ETIQUETAS_POR_LOTE = 6;   // bloques ^XA..^XZ por envío (cada bloque = 1 renglón de 3)
const MAX_CHARS_LOTE = 15000;   // tope de tamaño por envío
const PAUSA_MS = 250;           // respiro para que la impresora drene el buffer

const espera = (ms) => new Promise((r) => setTimeout(r, ms));

// Parte el ZPL en bloques completos ^XA...^XZ (nunca corta a la mitad de una etiqueta).
export function partirZPL(zpl, maxBloques = ETIQUETAS_POR_LOTE, maxChars = MAX_CHARS_LOTE) {
  const bloques = String(zpl || "").match(/\^XA[\s\S]*?\^XZ/g) || [];
  if (!bloques.length) return zpl ? [zpl] : [];
  const lotes = [];
  let actual = [];
  let largo = 0;
  for (const b of bloques) {
    // Cierra el lote si ya está lleno (por cantidad o por tamaño)
    if (actual.length && (actual.length >= maxBloques || largo + b.length > maxChars)) {
      lotes.push(actual.join(""));
      actual = [];
      largo = 0;
    }
    actual.push(b);
    largo += b.length;
  }
  if (actual.length) lotes.push(actual.join(""));
  return lotes;
}

// Resuelve UNA sola vez por dónde se va a imprimir (SDK o agente), para no
// re-descubrir la impresora en cada lote.
async function abrirCanal() {
  // 1) SDK oficial, si alguien lo cargó en la página
  const BP = typeof window !== "undefined" ? window.BrowserPrint : null;
  if (BP && BP.getDefaultDevice) {
    try {
      const device = await new Promise((resolve, reject) => {
        BP.getDefaultDevice(
          "printer",
          (d) => (d ? resolve(d) : reject(new Error("Sin impresora por defecto"))),
          (e) => reject(new Error(e || "No se pudo obtener la impresora"))
        );
      });
      return {
        via: "sdk",
        enviar: (texto) =>
          new Promise((resolve, reject) =>
            device.send(texto, () => resolve(), (e) => reject(new Error(e || "Error al enviar")))
          ),
      };
    } catch (_) {
      /* cae al agente */
    }
  }

  // 2) Agente Browser Print
  let ultimoError;
  for (const base of BASES_AGENTE) {
    try {
      const device = await obtenerDispositivo(base);
      // Envío de prueba vacío no: se valida con el primer lote real
      return {
        via: "agente",
        enviar: async (texto) => {
          const rw = await fetch(`${base}/write`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ device, data: texto }),
          });
          if (!rw.ok) throw new Error("Falló el envío a la impresora");
        },
      };
    } catch (e) {
      ultimoError = e;
    }
  }
  throw ultimoError || new Error("Zebra Browser Print no disponible");
}

// Descarga el ZPL como archivo (fallback / para previsualizar en Labelary).
export function descargarZPL(zpl, nombre = "etiquetas.zpl") {
  const blob = new Blob([zpl], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Imprime el ZPL completo, en lotes. `onProgreso(enviados, total)` es opcional.
// Lanza error si no hay por dónde imprimir (el llamador ofrece descargar el .zpl).
export async function imprimirZPL(zpl, onProgreso) {
  const canal = await abrirCanal();
  const lotes = partirZPL(zpl);
  for (let i = 0; i < lotes.length; i++) {
    await canal.enviar(lotes[i]);
    if (typeof onProgreso === "function") onProgreso(i + 1, lotes.length);
    // No hace falta esperar después del último
    if (i < lotes.length - 1) await espera(PAUSA_MS);
  }
  return canal.via;
}
